import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { EntityBadge, GroupIcon, OrgLogo, PersonAvatar } from "@/components/common/Visuals";
import { EmptyState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import { KINDS } from "@/lib/kinds";
import { formatNumber } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

function Section({ type, total, seeAll, children, testId }) {
  return (
    <section className="lm-card overflow-hidden lm-fade-up" data-testid={testId}>
      <div className="flex items-center justify-between border-b border-border/70 bg-stone-50/60 px-5 py-3">
        <div className="flex items-center gap-2.5">
          <EntityBadge type={type} />
          <span className="text-sm font-semibold text-stone-600">{formatNumber(total)} résultat{total > 1 ? "s" : ""}</span>
        </div>
        {seeAll && (
          <Link to={seeAll} className="inline-flex items-center gap-1 text-sm font-bold text-primary hover:underline" data-testid={`${testId}-see-all`}>
            Voir tout <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        )}
      </div>
      <div className="divide-y divide-border/60">{children}</div>
    </section>
  );
}

const Row = ({ to, visual, title, subtitle, testId }) => (
  <Link to={to} data-testid={testId} className="flex items-center gap-4 px-5 py-3 transition-colors hover:bg-accent/40">
    {visual}
    <div className="min-w-0">
      <div className="truncate font-semibold">{title}</div>
      {subtitle && <div className="truncate text-sm text-muted-foreground">{subtitle}</div>}
    </div>
  </Link>
);

export function SearchResults({ data, query }) {
  const { isAuthed } = useAuth();
  const q = encodeURIComponent(query);
  const groupKinds = Object.values(KINDS).filter((k) => data[k.key]?.total > 0);
  const total = data.persons.total + data.organizations.total + groupKinds.reduce((s, k) => s + data[k.key].total, 0);
  if (!total) {
    return (
      <EmptyState
        description={`Aucune personne, entreprise ou communauté ne correspond à « ${query} ».`}
        action={isAuthed && <Button asChild className="rounded-full" data-testid="empty-add-person-button"><Link to="/personnes/nouveau">Ajouter une personne</Link></Button>}
      />
    );
  }
  return (
    <div className="grid gap-5 lg:grid-cols-2" data-testid="search-results">
      {data.persons.total > 0 && (
        <Section type="person" total={data.persons.total} seeAll={`/personnes?q=${q}`} testId="search-section-persons">
          {data.persons.items.map((p) => (
            <Row key={p.id} to={`/personnes/${p.id}`} testId={`search-result-person-${p.id}`} visual={<PersonAvatar person={p} size="sm" className="rounded-xl" />}
              title={p.full_name} subtitle={[p.job_title, p.organizations.map((o) => o.name).join(", ")].filter(Boolean).join(" · ")} />
          ))}
        </Section>
      )}
      {data.organizations.total > 0 && (
        <Section type="organization" total={data.organizations.total} seeAll={`/entreprises?q=${q}`} testId="search-section-organizations">
          {data.organizations.items.map((o) => (
            <Row key={o.id} to={`/entreprises/${o.id}`} testId={`search-result-org-${o.id}`} visual={<OrgLogo org={o} size="sm" className="rounded-xl" />}
              title={o.name} subtitle={[o.org_type.join(", "), o.department.join(", "), `${o.contacts_count} contact${o.contacts_count > 1 ? "s" : ""}`].filter(Boolean).join(" · ")} />
          ))}
        </Section>
      )}
      {groupKinds.map((k) => (
        <Section key={k.key} type={k.key} total={data[k.key].total} testId={`search-section-${k.key}`}>
          {data[k.key].items.map((g) => (
            <Row key={g.id} to={`${k.path}/${g.id}`} testId={`search-result-${k.key}-${g.id}`}
              visual={<span className="h-9 w-9 shrink-0 rounded-xl"><GroupIcon group={g} box /></span>}
              title={g.name} subtitle={k.singular} />
          ))}
        </Section>
      ))}
    </div>
  );
}
